import React, {
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { useModal } from "react-modal-hook";
import { buildGuestWorkflowURL } from "common/utils/url";
import {
  AccessControlEntityType,
  AccessControlPolicy,
  useSyncWorkflowCollaboratorsMutation,
  useWorkflowCollaboratorsQuery,
  useWorkflowStepCollaboratorsQuery,
} from "../../generated/graphql";
import { useStatusToasts } from "../../hooks/useStatusToasts";
import { spacing } from "../../stitches.config";
import { track } from "../../utils/tracking";
import { AuthContext } from "../Authorization/AuthContext";
import { CommonModal } from "../Common/CommonModal";
import { Loading } from "../Common/Icons/Loading";
import Modal from "../Common/Modal";
import {
  Collaborator,
  InheritedPermissionGroup,
  PermissionOption,
  ReadOnlyCollaboratorRow,
  ShareForm,
} from "../Common/ShareForm";
import { CollaboratorListContainer } from "../Common/ShareForm/__styles__/CollaboratorList";
import { Body } from "../Common/Typography";

export type ShareableEntityType = "workflow" | "workflowStep";

type ModalView = "main" | "inheritedPermissions";

export interface ShareWorkflowModalProps {
  entityType: ShareableEntityType;
  entityId: string;
  workflowId: string;
  entityName: string;
  workflowName?: string;
  onClose: () => void;
  clipboard?: Pick<Clipboard, "writeText">;
  onInvite?: (collaborators: Collaborator[]) => void;
  onRemove?: (collaboratorId: string) => void;
}

const WORKFLOW_PERMISSION_OPTIONS: PermissionOption[] = [
  { value: "createResource", label: "Can create" },
  { value: "updateResource", label: "Can update" },
  { value: "viewResource", label: "Can view" },
];

const getModalTitle = (entityType: ShareableEntityType): string => {
  switch (entityType) {
    case "workflow":
      return "Share workflow";
    case "workflowStep":
      return "Share step";
    default:
      return "Share";
  }
};

const getInfoText = (entityType: ShareableEntityType) => {
  const entityLabel = entityType === "workflow" ? "workflow" : "step";
  return {
    text: `All Forerunner team members can view this ${entityLabel}.`,
    linkText: "Add a team member",
    linkHref: "/settings/users/team",
  };
};

interface InheritedPermissionsViewProps {
  entityName: string;
  group: InheritedPermissionGroup;
  onBack: () => void;
  onClose: () => void;
}

const InheritedPermissionsView = ({
  entityName,
  group,
  onBack,
  onClose,
}: InheritedPermissionsViewProps) => {
  return (
    <CommonModal
      title="Share workflow step"
      subtitle={entityName}
      size="large"
      onCancel={onClose}
      handlePrevious={onBack}
      content={
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: spacing.l.value,
          }}
        >
          <Body
            type="regular"
            size="default"
            color="contentSecondary"
            style={{ paddingLeft: spacing.m.value }}
          >
            The following people have access to this step because they are
            members of the workflow.
          </Body>
          <CollaboratorListContainer>
            {group.collaborators.map(collaborator => (
              <ReadOnlyCollaboratorRow
                key={collaborator.id}
                collaborator={collaborator}
                permissionOptions={WORKFLOW_PERMISSION_OPTIONS}
              />
            ))}
          </CollaboratorListContainer>
        </div>
      }
    />
  );
};

export const ShareWorkflowModalContent = ({
  entityType,
  entityId,
  workflowId,
  entityName,
  workflowName,
  onClose,
  clipboard = navigator.clipboard,
}: Omit<ShareWorkflowModalProps, "onInvite" | "onRemove">) => {
  const { account } = useContext(AuthContext);
  const { addSuccessToast, addErrorToast } = useStatusToasts();
  const [currentView, setCurrentView] = useState<ModalView>("main");
  const [selectedInheritedGroup, setSelectedInheritedGroup] =
    useState<InheritedPermissionGroup | null>(null);

  const { data: workflowData, loading: workflowLoading } =
    useWorkflowCollaboratorsQuery({
      variables: {
        workflowId: entityType === "workflow" ? entityId : workflowId,
      },
      skip: entityType !== "workflow",
      fetchPolicy: "no-cache",
      notifyOnNetworkStatusChange: true,
    });

  const { data: stepData, loading: stepLoading } =
    useWorkflowStepCollaboratorsQuery({
      variables: { workflowStepId: entityId },
      skip: entityType !== "workflowStep",
      fetchPolicy: "no-cache",
      notifyOnNetworkStatusChange: true,
    });

  // For workflow steps, fetch the parent workflow's collaborators for inherited permissions
  const { data: parentWorkflowData } = useWorkflowCollaboratorsQuery({
    variables: { workflowId: workflowId! },
    skip: entityType !== "workflowStep" || !workflowId,
    fetchPolicy: "no-cache",
  });

  const isLoading = entityType === "workflow" ? workflowLoading : stepLoading;

  const [syncCollaborators, { loading: isSubmitting }] =
    useSyncWorkflowCollaboratorsMutation();

  const mapCollaboratorsFromData = useCallback(
    (
      collaborators:
        | NonNullable<typeof workflowData>["workflowCollaborators"]
        | NonNullable<typeof stepData>["workflowStepCollaborators"]
        | undefined,
    ): Collaborator[] => {
      if (!collaborators) return [];

      const permissionRank: Record<string, number> = {
        createResource: 3,
        updateResource: 2,
        viewResource: 1,
      };

      const collaboratorMap = new Map<string, Collaborator>();

      collaborators.forEach(collab => {
        const user = collab.user ?? collab.publicUser;
        const name =
          user ? `${user.firstName} ${user.lastName}`.trim() : "Unknown";
        const description = user?.email;

        const existing = collaboratorMap.get(collab.entityId);
        const currentRank = permissionRank[collab.policy] || 0;
        const existingRank =
          existing ? permissionRank[existing.permission] || 0 : -1;

        if (currentRank > existingRank) {
          collaboratorMap.set(collab.entityId, {
            id: collab.entityId,
            name: name || description || "Unknown",
            description,
            permission: collab.policy,
          });
        }
      });

      return Array.from(collaboratorMap.values()).sort((a, b) =>
        a.name.localeCompare(b.name),
      );
    },
    [],
  );

  const serverCollaborators = useMemo<Collaborator[]>(() => {
    const collaborators =
      entityType === "workflow" ?
        workflowData?.workflowCollaborators
      : stepData?.workflowStepCollaborators;

    return mapCollaboratorsFromData(collaborators);
  }, [
    entityType,
    workflowData?.workflowCollaborators,
    stepData?.workflowStepCollaborators,
    mapCollaboratorsFromData,
  ]);

  // Create inherited permissions group for workflow steps
  const inheritedPermissions = useMemo<
    InheritedPermissionGroup[] | undefined
  >(() => {
    if (entityType !== "workflowStep" || !workflowId) return undefined;

    const workflowCollaborators = mapCollaboratorsFromData(
      parentWorkflowData?.workflowCollaborators,
    );

    if (workflowCollaborators.length === 0) return undefined;

    return [
      {
        id: workflowId,
        label: "Anyone in the workflow",
        description: workflowName,
        collaborators: workflowCollaborators,
      },
    ];
  }, [
    entityType,
    workflowId,
    workflowName,
    parentWorkflowData?.workflowCollaborators,
    mapCollaboratorsFromData,
  ]);

  const currentCollaboratorsRef = useRef<Collaborator[]>([]);

  const handleStateChange = useCallback((collaborators: Collaborator[]) => {
    currentCollaboratorsRef.current = collaborators;
  }, []);

  const handleCopyLink = useCallback(async () => {
    const url = buildGuestWorkflowURL({
      subdomain: account!.subdomain,
      workflowId,
    });
    await clipboard.writeText(url);
    addSuccessToast("Link copied to clipboard");
  }, [account, workflowId, clipboard, addSuccessToast]);

  const entityLabel = entityType === "workflow" ? "workflow" : "step";
  const handleSubmit = useCallback(async () => {
    try {
      await syncCollaborators({
        variables: {
          data: {
            workflowId,
            workflowStepId:
              entityType === "workflowStep" ? entityId : undefined,
            collaborators: currentCollaboratorsRef.current.map(c => ({
              entityId: c.id,
              entityType: AccessControlEntityType.PUBLIC_USER,
              policy: c.permission as AccessControlPolicy,
            })),
          },
        },
      });

      const eventName =
        entityType === "workflow" ? "Shared Workflow" : "Shared Workflow Step";
      track(eventName, {
        workflowId,
        workflowStepId: entityType === "workflowStep" ? entityId : undefined,
      });

      addSuccessToast(`This ${entityLabel} has been updated successfully!`);

      onClose();
    } catch {
      addErrorToast(`Failed to update ${entityLabel}`);
    }
  }, [
    workflowId,
    entityType,
    entityId,
    entityLabel,
    syncCollaborators,
    addSuccessToast,
    addErrorToast,
    onClose,
  ]);

  const handleInheritedPermissionGroupClick = useCallback(
    (group: InheritedPermissionGroup) => {
      setSelectedInheritedGroup(group);
      setCurrentView("inheritedPermissions");
    },
    [],
  );

  const handleBackToMain = useCallback(() => {
    setCurrentView("main");
    setSelectedInheritedGroup(null);
  }, []);

  if (currentView === "inheritedPermissions" && selectedInheritedGroup) {
    return (
      <InheritedPermissionsView
        entityName={entityName}
        group={selectedInheritedGroup}
        onBack={handleBackToMain}
        onClose={onClose}
      />
    );
  }

  return (
    <CommonModal
      title={getModalTitle(entityType)}
      subtitle={entityName}
      size="large"
      onCancel={onClose}
      primaryButtonText={isSubmitting ? "Saving..." : "Share"}
      onSubmit={handleSubmit}
      disabled={isSubmitting || isLoading}
      secondaryButtons={[
        {
          text: "Copy link",
          onClick: handleCopyLink,
        },
      ]}
      content={
        isLoading ?
          <Loading size="medium" />
        : <ShareForm
            searchPlaceholder="Add email address"
            sectionLabel="Who has access"
            infoText={getInfoText(entityType)}
            permissionOptions={WORKFLOW_PERMISSION_OPTIONS}
            initialCollaborators={serverCollaborators}
            onStateChange={handleStateChange}
            buttonText="Add"
            showRemoveOption={true}
            includeUsers={false}
            includePublicUsers={true}
            inheritedPermissions={inheritedPermissions}
            onInheritedPermissionGroupClick={
              handleInheritedPermissionGroupClick
            }
          />
      }
    />
  );
};

export const useShareWorkflowModal = ({
  entityType,
  entityId,
  workflowId,
  entityName,
  workflowName,
  clipboard = navigator.clipboard,
}: Omit<ShareWorkflowModalProps, "onClose" | "onInvite" | "onRemove">) => {
  const [showModal, hideModal] = useModal(
    () => (
      <Modal onRequestClose={hideModal}>
        <ShareWorkflowModalContent
          entityType={entityType}
          entityId={entityId}
          workflowId={workflowId}
          entityName={entityName}
          workflowName={workflowName}
          onClose={hideModal}
          clipboard={clipboard}
        />
      </Modal>
    ),
    [entityType, entityId, workflowId, entityName, workflowName, clipboard],
  );

  return [showModal, hideModal] as const;
};
